import type { CostProvenance, CostQuality, LaneTotals } from "@cmmd-forge/shared/usageMerge";
import { formatTokens, formatUsd } from "@cmmd-forge/shared/usageFormat";

import { usageCoverageClauses } from "@cmmd-forge/shared/usageCoverageCopy";
import { usageLaneRows } from "./usageLaneRows";

/**
 * The lanes that are not yet, or never will be, in CMMD's ledger, and who
 * priced the total. Lane rows render only when they hold something.
 *
 * The coverage clauses sit under the priced-by line because they qualify it:
 * a window can be fully reported and still hold turns nobody could price, and
 * without the counts those turns read as free.
 */
export function UsageLanes({
  pendingSync,
  localOnly,
  forgeAgents,
  costProvenance,
  costQuality,
}: {
  readonly pendingSync: LaneTotals;
  readonly localOnly: LaneTotals;
  readonly forgeAgents: LaneTotals;
  readonly costProvenance: CostProvenance;
  readonly costQuality: CostQuality;
}) {
  const lanes = usageLaneRows(pendingSync, localOnly, forgeAgents);
  const coverage = usageCoverageClauses(costQuality);

  return (
    <section className="flex flex-col gap-2" data-testid="usage-lanes">
      <h2 className="text-sm font-medium text-foreground">Where the numbers come from</h2>
      <p className="text-xs text-muted-foreground">
        {formatUsd(costProvenance.cmmdUsd)} priced by CMMD · {formatUsd(costProvenance.localUsd)}{" "}
        estimated on this machine
      </p>
      {coverage.length > 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="usage-coverage-clauses">
          {coverage.join(" · ")}
        </p>
      ) : null}
      {lanes.length > 0 ? (
        <dl className="grid gap-x-6 gap-y-3 py-1 md:grid-cols-2">
          {lanes.map((lane) => (
            <div key={lane.key} className="flex min-w-0 flex-col gap-0.5">
              <dt className="text-xs text-muted-foreground">{lane.label}</dt>
              <dd className="flex items-baseline gap-2">
                <span className="text-base font-medium text-foreground tabular-nums">
                  {formatUsd(lane.totals.costUsd)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatTokens(lane.totals.totalTokens)} tokens · {lane.detail}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </section>
  );
}
